import {
  AutotractorSlotIDs,
  InteractionLayer,
  MapImageId,
} from "common/components/map/layers/types";
import { Bounds } from "portal/utils/geo";
import { CenterPivotLeg } from "protos/farm/farm";
import { FarmMapData, PointFeature, ZoneFeature } from "common/utils/farm";
import { Map as PortalMap } from "portal/components/map/Map";
import { useFarmLayers } from "common/components/map/layers/FarmLayers";
import { useMapSlots } from "common/components/map/MapSlots";
import { viteImageResolver } from "common/utils/map/imageResolver.vite";
import React, {
  ComponentProps,
  FunctionComponent,
  ReactNode,
  useMemo,
} from "react";

/** something drawn on the farm map alongside the farm itself */
export interface MapExtension {
  layers: ReactNode;
  interactionLayers: InteractionLayer[];
  idsToLoad: MapImageId[];
  mapProps: Pick<
    ComponentProps<typeof PortalMap>,
    "onMapMouseMove" | "onMapMouseLeave" | "defaultCursor"
  >;
}

interface FarmMapProps {
  selectedPointId: string | undefined;
  selectedZoneIds: string[];
  focusedPointId: string | undefined;
  focusedZoneIds: string[];
  shownPoints?: string[];
  farmsMapData: FarmMapData[];
  /** legs to draw in place of a pivot's stored ones */
  centerPivotLegsByZoneId?: Record<string, CenterPivotLeg[]>;
  className?: string;
  onPointClick?: (feature?: PointFeature) => void;
  onPointHover?: (feature?: PointFeature) => void;
  onZoneClick?: (feature?: ZoneFeature) => void;
  onZoneHover?: (feature?: ZoneFeature) => void;
  onMapClick?: React.ComponentProps<typeof PortalMap>["onMapClick"];
  onMapReady?: React.ComponentProps<typeof PortalMap>["onMapReady"];
  extension?: MapExtension;
  bounds?: Bounds;
  defaultCursor?: string;
}
const SLOT_IDS = Object.values(AutotractorSlotIDs);

export const FarmMap: FunctionComponent<FarmMapProps> = ({
  selectedPointId,
  selectedZoneIds,
  focusedPointId,
  focusedZoneIds,
  shownPoints,
  farmsMapData,
  centerPivotLegsByZoneId,
  className,
  onPointClick,
  onPointHover,
  onZoneClick,
  onZoneHover,
  onMapClick,
  onMapReady,
  extension,
  defaultCursor,
  bounds,
}) => {
  const {
    idsToLoad,
    interactionLayers,
    Layers: FarmLayers,
  } = useFarmLayers({
    clickPoint: onPointClick,
    hoverPoint: onPointHover,
    clickZone: onZoneClick,
    hoverZone: onZoneHover,
    leaveZone: () => onZoneHover?.(undefined),
  });
  const mapSlots = useMapSlots(SLOT_IDS);

  const effectiveBounds = useMemo(() => {
    if (bounds) {
      return bounds;
    } else {
      // compute bounding box that contains all of the individual farm bounding
      // boxes
      const computedBounds = {
        minX: Infinity,
        maxX: -Infinity,
        minY: Infinity,
        maxY: -Infinity,
      };

      for (const fmd of farmsMapData) {
        if (fmd.bounds) {
          computedBounds.minX = Math.min(computedBounds.minX, fmd.bounds.minX);
          computedBounds.minY = Math.min(computedBounds.minY, fmd.bounds.minY);
          computedBounds.maxX = Math.max(computedBounds.maxX, fmd.bounds.maxX);
          computedBounds.maxY = Math.max(computedBounds.maxY, fmd.bounds.maxY);
        }
      }

      return computedBounds;
    }
  }, [bounds, farmsMapData]);

  return (
    <PortalMap
      className={className}
      hideRobots
      hideMeasure
      hideHoverInfo
      allowEmpty
      imageConfig={{
        resolver: viteImageResolver,
        idsToLoad: extension
          ? [...new Set([...idsToLoad, ...extension.idsToLoad])]
          : idsToLoad,
      }}
      interactionLayers={
        extension
          ? [...interactionLayers, ...extension.interactionLayers]
          : interactionLayers
      }
      extraControls={
        <>
          {mapSlots}
          <FarmLayers
            farmsMapData={farmsMapData}
            shownPoints={shownPoints ?? []}
            selectedPointId={selectedPointId}
            focusedPointId={focusedPointId}
            focusedZoneIds={focusedZoneIds}
            selectedZoneIds={selectedZoneIds}
            centerPivotLegsByZoneId={centerPivotLegsByZoneId}
            showUntrackedPivotArms
            // slotted so pivots draw above zones and obstacles
            beforeId={AutotractorSlotIDs.FARMS}
            slotIds={SLOT_IDS}
          />
          {extension?.layers}
        </>
      }
      extraBounds={effectiveBounds}
      onMapClick={onMapClick}
      onMapReady={onMapReady}
      defaultCursor={defaultCursor}
      {...extension?.mapProps}
    />
  );
};
